import { useCallback, useEffect, useMemo, useState } from "react";
import { Head } from "vite-react-ssg";
import type { Session } from "@supabase/supabase-js";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Loader2, LogOut, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

/**
 * Beheerpaneel van ChatBuddy (de WhatsApp-buddy). Niet geïndexeerd en nergens gelinkt.
 * Inloggen met een e-maillink; alleen adressen die de functie buddy-admin kent zien cijfers.
 */

const ADMIN_URL = "https://fnlfrdinxtwkhzbmyawv.supabase.co/functions/v1/buddy-admin";

type Kerncijfers = Record<string, number>;
type Dag = { dag: string; zelf: number; via_vriend: number; berichten: number; kosten_usd: number };
type Sjabloon = { naam: string; taal: string; status: string; categorie: string; reden: string | null };
type Overzicht = {
  bijgewerkt: string;
  kerncijfers: Kerncijfers;
  per_dag: Dag[];
  beoordelingen: { oordeel: string; aantal: number }[];
  feedback: { id: number; tijd: string; oordeel: string; taal: string | null; tekst: string }[];
  talen: { taal: string; aantal: number }[];
  uitnodigingen: Record<string, number>;
  ochtend: {
    aan: number;
    aangezet_7d: number;
    afgeslagen_7d: number;
    verstuurd_gratis_7d: number;
    verstuurd_sjabloon_7d: number;
    overgeslagen_7d: Record<string, number>;
  };
  gebruik?: {
    berichten: { soort: string; aantal: number; mensen: number }[];
    acties: { actie: string; aantal: number; mensen: number }[];
    zoeken: number;
    zoeken_mensen: number;
    slim_model: number;
    slim_model_mensen: number;
  };
  meta_kosten: { soort: string; betaald: boolean | null; aantal: number }[];
  meta_fouten: { code: number; aantal: number }[];
  sjablonen: Sjabloon[] | { fout: string };
  actief: Record<string, { naam: string; talen: string[] }>;
};

const nf = new Intl.NumberFormat("nl-NL");
const n = (v: number | undefined | null) => nf.format(Number(v ?? 0));
const usd = (v: number | undefined | null) =>
  new Intl.NumberFormat("nl-NL", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(v ?? 0));

const TAALNAMEN: Record<string, string> = {
  nl: "Nederlands", en: "Engels", de: "Duits", fr: "Frans", es: "Spaans", it: "Italiaans",
  pt: "Portugees", pt_PT: "Portugees", pl: "Pools", ro: "Roemeens", tr: "Turks",
};
const taal = (code: string | null) => (code ? TAALNAMEN[code] ?? code : "?");

const SJABLOONNAMEN: Record<string, string> = {
  buddy_uitnodiging: "Uitnodiging (oud)",
  buddy_uitnodiging_v2: "Uitnodiging",
  buddy_herinnering: "Herinnering",
  buddy_ochtend: "Ochtendbericht",
};

const STATUS: Record<string, { label: string; cls: string }> = {
  APPROVED: { label: "goedgekeurd", cls: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" },
  PENDING: { label: "in behandeling", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  REJECTED: { label: "afgewezen", cls: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
  PAUSED: { label: "gepauzeerd", cls: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
  DISABLED: { label: "uitgezet", cls: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
};

const INVITE_STATUS: Record<string, string> = {
  wachtrij: "In de wachtrij",
  bezig: "Wordt verstuurd",
  verstuurd: "Verstuurd",
  gestart: "Begonnen",
  mislukt: "Mislukt",
  afgemeld: "Afgemeld",
};

const OVERGESLAGEN: Record<string, string> = {
  gratis_buiten_venster: "Gratis, geen bericht in 24 uur",
  sjabloon_niet_goedgekeurd: "Sjabloon nog niet goedgekeurd",
};

const BERICHTSOORT: Record<string, string> = {
  text: "Getypt",
  audio: "Spraakbericht",
  image: "Foto",
  document: "Document",
  location: "Locatie",
  button: "Knop",
  other: "Iets anders",
};

// Wat de buddy voor gebruikers deed. Interne acties (bonussen, abonnementsstatus) staan er niet in.
const ACTIE: Record<string, string> = {
  herinnering_gepland: "Herinnering gepland",
  herinnering_geannuleerd: "Herinnering geannuleerd",
  lijst_bijgewerkt: "Lijstje bijgewerkt",
  onthouden: "Iets onthouden",
  profiel_bijgewerkt: "Profiel aangevuld",
  vergeten: "Iets laten vergeten",
  alles_vergeten: "Alles laten vergeten",
  beeld_gemaakt: "Beeld gemaakt",
  instellingen: "Instelling gewijzigd",
  taal: "Andere taal gekozen",
  ochtendbericht_aan: "Ochtendbericht aangezet",
  ochtendbericht_nee: "Ochtendbericht afgeslagen",
  uitnodiging_gedeeld: "Vrienden uitgenodigd",
  feedback: "Feedback gegeven",
  beoordeling: "Beoordeling gegeven",
  export: "Gegevens gedownload",
  betaallink_gestuurd: "Betaallink gevraagd",
  beheerlink_gestuurd: "Abonnement beheerd",
};

function Gebruik({ g }: { g: NonNullable<Overzicht["gebruik"]> }) {
  const totaal = g.berichten.reduce((a, b) => a + b.aantal, 0);
  const acties = [
    ...g.acties.filter((a) => ACTIE[a.actie]).map((a) => ({ wat: ACTIE[a.actie], aantal: a.aantal, mensen: a.mensen })),
    ...(g.zoeken ? [{ wat: "Op internet gezocht", aantal: g.zoeken, mensen: g.zoeken_mensen }] : []),
    ...(g.slim_model ? [{ wat: "Slimste model ingezet", aantal: g.slim_model, mensen: g.slim_model_mensen }] : []),
  ].sort((a, b) => b.mensen - a.mensen || b.aantal - a.aantal);

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div>
        <h3 className="mb-2 text-sm font-medium">Wat ze sturen</h3>
        {totaal ? (
          <ul className="space-y-2 text-sm">
            {g.berichten.map((b) => (
              <li key={b.soort} className="flex items-center gap-3">
                <span className="w-28 shrink-0">{BERICHTSOORT[b.soort] ?? b.soort}</span>
                <span className="h-2 flex-1 rounded bg-muted">
                  <span className="block h-2 rounded bg-primary" style={{ width: `${(b.aantal / totaal) * 100}%` }} />
                </span>
                <span className="w-24 shrink-0 text-right tabular-nums text-xs text-muted-foreground">
                  {n(b.aantal)} · {n(b.mensen)} {b.mensen === 1 ? "persoon" : "mensen"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <Leeg>Nog geen berichten.</Leeg>
        )}
      </div>
      <div>
        <h3 className="mb-2 text-sm font-medium">Wat de buddy voor ze deed</h3>
        {acties.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Wat</TableHead>
                <TableHead className="text-right">Keer</TableHead>
                <TableHead className="text-right">Mensen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {acties.map((a) => (
                <TableRow key={a.wat}>
                  <TableCell className="text-sm">{a.wat}</TableCell>
                  <TableCell className="text-right tabular-nums">{n(a.aantal)}</TableCell>
                  <TableCell className="text-right tabular-nums">{n(a.mensen)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Leeg>Nog niets.</Leeg>
        )}
      </div>
    </div>
  );
}

function Tegel({ label, waarde, uitleg }: { label: string; waarde: string; uitleg?: string }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{waarde}</div>
      {uitleg && <div className="mt-1 text-xs text-muted-foreground">{uitleg}</div>}
    </Card>
  );
}

function Blok({ titel, children, uitleg }: { titel: string; children: React.ReactNode; uitleg?: string }) {
  return (
    <Card className="p-5">
      <h2 className="text-base font-semibold">{titel}</h2>
      {uitleg && <p className="mt-0.5 text-xs text-muted-foreground">{uitleg}</p>}
      <div className="mt-4">{children}</div>
    </Card>
  );
}

function Leeg({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function Inloggen() {
  const [email, setEmail] = useState("");
  const [bezig, setBezig] = useState(false);
  const [verstuurd, setVerstuurd] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const verstuur = async (e: React.FormEvent) => {
    e.preventDefault();
    setBezig(true);
    setFout(null);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: `${window.location.origin}/admin`, shouldCreateUser: false },
    });
    setBezig(false);
    if (error) setFout("Dat lukte niet. Controleer het adres en probeer het nog eens.");
    else setVerstuurd(true);
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-16">
      <Card className="w-full max-w-sm p-6">
        <h1 className="text-xl font-semibold">ChatBuddy beheer</h1>
        {verstuurd ? (
          <p className="mt-4 text-sm">
            Als <span className="font-medium">{email}</span> toegang heeft, staat er nu een inloglink in je mail.
            Open hem op dit apparaat.
          </p>
        ) : (
          <form onSubmit={verstuur} className="mt-4 space-y-3">
            <label htmlFor="admin-email" className="block text-sm font-medium">
              E-mailadres
            </label>
            <Input
              id="admin-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" className="w-full" disabled={bezig}>
              {bezig ? "Bezig…" : "Stuur me een inloglink"}
            </Button>
            {fout && <p className="text-sm text-destructive">{fout}</p>}
          </form>
        )}
      </Card>
    </main>
  );
}

function Paneel({ data, bezig, vernieuw, email }: { data: Overzicht; bezig: boolean; vernieuw: () => void; email: string }) {
  const k = data.kerncijfers;
  const perSjabloon = useMemo(() => {
    const m = new Map<string, Sjabloon[]>();
    for (const s of Array.isArray(data.sjablonen) ? data.sjablonen : []) m.set(s.naam, [...(m.get(s.naam) ?? []), s]);
    return [...m.entries()];
  }, [data.sjablonen]);
  const dagen = data.per_dag.map((d) => ({ ...d, label: d.dag.slice(8, 10) + "-" + d.dag.slice(5, 7) }));
  const uitnodigingen = Object.entries(data.uitnodigingen ?? {});
  const overgeslagen = Object.entries(data.ochtend?.overgeslagen_7d ?? {});
  const beoordeeld = data.beoordelingen.reduce((a, b) => a + b.aantal, 0);

  return (
    <main className="min-h-screen bg-muted/30">
      <header className="border-b bg-background">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <div>
            <h1 className="text-lg font-semibold">ChatBuddy beheer</h1>
            <p className="text-xs text-muted-foreground">
              Bijgewerkt {new Date(data.bijgewerkt).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" })} ·
              alleen echte WhatsApp-gebruikers, zonder namen of nummers
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={vernieuw} disabled={bezig}>
              <RefreshCw className={`mr-1.5 h-4 w-4 ${bezig ? "animate-spin" : ""}`} />
              Vernieuwen
            </Button>
            <Button variant="ghost" size="sm" onClick={() => supabase.auth.signOut()} title={email}>
              <LogOut className="mr-1.5 h-4 w-4" />
              Uitloggen
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl space-y-6 px-4 py-6">
        <section>
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Van wachtlijst tot gebruiker</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Tegel label="Wachtlijst" waarde={n(k.wachtlijst)} />
            <Tegel label="Uitgenodigd" waarde={n(k.uitgenodigd)} uitleg={k.in_wachtrij ? `${n(k.in_wachtrij)} in de wachtrij` : undefined} />
            <Tegel label="Begonnen" waarde={n(k.gestart)} uitleg="stuurde een bericht" />
            <Tegel label="Akkoord" waarde={n(k.akkoord)} uitleg="voorwaarden geaccepteerd" />
            <Tegel label="Actief vandaag" waarde={n(k.actief_vandaag)} />
            <Tegel label="Actief 7 dagen" waarde={n(k.actief_7d)} />
          </div>
        </section>

        <section>
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Geld en gebruik</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Tegel label="Betalend" waarde={n(k.betalend)} uitleg={`${n(k.betalend_plus)} Plus · ${n(k.betalend_max)} Max`} />
            <Tegel label="Gratis Plus" waarde={n(k.gratis_plus)} uitleg="wachtlijstmaand of beloning" />
            <Tegel label="Via een vriend" waarde={n(k.via_vriend)} uitleg={`${n(k.beloond)} beloond`} />
            <Tegel label="Ochtendbericht aan" waarde={n(k.ochtend_aan)} />
            <Tegel label="Berichten 30 dagen" waarde={n(k.berichten_30d)} />
            <Tegel label="AI-kosten 30 dagen" waarde={usd(k.kosten_ai_30d)} uitleg={`${n(k.sjablonen_30d)} sjabloonberichten`} />
          </div>
        </section>

        <Blok titel="Per dag" uitleg="Nieuwe gebruikers (zelf of via een vriend) en hun berichten, laatste 30 dagen">
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dagen} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
                <YAxis yAxisId="g" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="b" orientation="right" allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v: number, naam: string) => [n(v), naam]}
                  labelFormatter={(l: string) => `Dag ${l}`}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="g" dataKey="zelf" name="Zelf gekomen" stackId="g" fill="hsl(213 100% 50%)" />
                <Bar yAxisId="g" dataKey="via_vriend" name="Via een vriend" stackId="g" fill="hsl(152 60% 42%)" radius={[3, 3, 0, 0]} />
                <Line yAxisId="b" dataKey="berichten" name="Berichten" stroke="hsl(32 95% 50%)" strokeWidth={2} dot={false} type="linear" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Blok>

        {data.gebruik && (
          <Blok titel="Waarvoor ze ChatBuddy gebruiken" uitleg="Laatste 30 dagen. Alleen soorten en aantallen, nooit wat erin stond.">
            <Gebruik g={data.gebruik} />
          </Blok>
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          <Blok titel="Beoordelingen" uitleg="Antwoord op de vraag hoe ChatBuddy bevalt">
            {beoordeeld ? (
              <ul className="space-y-2 text-sm">
                {data.beoordelingen.map((b) => (
                  <li key={b.oordeel} className="flex items-center gap-3">
                    <span className="w-20">{b.oordeel}</span>
                    <span className="h-2 flex-1 rounded bg-muted">
                      <span className="block h-2 rounded bg-primary" style={{ width: `${(b.aantal / beoordeeld) * 100}%` }} />
                    </span>
                    <span className="w-10 text-right tabular-nums">{n(b.aantal)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <Leeg>Nog geen beoordelingen.</Leeg>
            )}
          </Blok>

          <Blok titel="Talen" uitleg="Gebruikers die akkoord gaven">
            {data.talen.length ? (
              <ul className="space-y-1 text-sm">
                {data.talen.map((t) => (
                  <li key={t.taal} className="flex justify-between">
                    <span>{taal(t.taal)}</span>
                    <span className="tabular-nums">{n(t.aantal)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <Leeg>Nog niemand.</Leeg>
            )}
          </Blok>

          <Blok titel="Uitnodigingen" uitleg="Wachtlijst via WhatsApp">
            {uitnodigingen.length ? (
              <ul className="space-y-1 text-sm">
                {uitnodigingen.map(([status, aantal]) => (
                  <li key={status} className="flex justify-between">
                    <span>{INVITE_STATUS[status] ?? status}</span>
                    <span className="tabular-nums">{n(aantal)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <Leeg>Er zijn nog geen uitnodigingen verstuurd.</Leeg>
            )}
          </Blok>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Blok titel="Ochtendbericht" uitleg="Laatste 7 dagen">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Staat aan</dt>
              <dd className="text-right tabular-nums">{n(data.ochtend.aan)}</dd>
              <dt className="text-muted-foreground">Ja gezegd</dt>
              <dd className="text-right tabular-nums">{n(data.ochtend.aangezet_7d)}</dd>
              <dt className="text-muted-foreground">Nee gezegd</dt>
              <dd className="text-right tabular-nums">{n(data.ochtend.afgeslagen_7d)}</dd>
              <dt className="text-muted-foreground">Verstuurd, gratis</dt>
              <dd className="text-right tabular-nums">{n(data.ochtend.verstuurd_gratis_7d)}</dd>
              <dt className="text-muted-foreground">Verstuurd via sjabloon</dt>
              <dd className="text-right tabular-nums">{n(data.ochtend.verstuurd_sjabloon_7d)}</dd>
              {overgeslagen.map(([reden, aantal]) => (
                <div key={reden} className="contents">
                  <dt className="text-muted-foreground">Overgeslagen: {OVERGESLAGEN[reden] ?? reden}</dt>
                  <dd className="text-right tabular-nums">{n(aantal)}</dd>
                </div>
              ))}
            </dl>
          </Blok>

          <Blok titel="Wat Meta rekent" uitleg="Per verstuurd bericht, laatste 30 dagen">
            {data.meta_kosten.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Soort</TableHead>
                    <TableHead>Betaald</TableHead>
                    <TableHead className="text-right">Berichten</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.meta_kosten.map((r) => (
                    <TableRow key={`${r.soort}-${String(r.betaald)}`}>
                      <TableCell>{r.soort}</TableCell>
                      <TableCell>{r.betaald === null ? "?" : r.betaald ? "ja" : "nee"}</TableCell>
                      <TableCell className="text-right tabular-nums">{n(r.aantal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Leeg>Nog geen verstuurde berichten met prijsinformatie.</Leeg>
            )}
            {data.meta_fouten.length > 0 && (
              <p className="mt-3 text-xs text-muted-foreground">
                Niet aangekomen: {data.meta_fouten.map((f) => `${n(f.aantal)}× code ${f.code}`).join(", ")}
              </p>
            )}
          </Blok>
        </div>

        <Blok titel="Sjablonen bij Meta" uitleg="Goedkeuring per taal; vetgedrukt = in gebruik door de buddy">
          {!Array.isArray(data.sjablonen) ? (
            <Leeg>De stand bij Meta kon niet worden opgehaald.</Leeg>
          ) : (
            <div className="space-y-4">
              {perSjabloon.map(([naam, talen]) => {
                const actief = Object.values(data.actief ?? {}).find((a) => a.naam === naam)?.talen ?? [];
                return (
                  <div key={naam}>
                    <div className="mb-1.5 text-sm font-medium">
                      {SJABLOONNAMEN[naam] ?? naam}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">{talen[0]?.categorie?.toLowerCase()}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {talen.map((t) => {
                        const s = STATUS[t.status] ?? { label: t.status.toLowerCase(), cls: "bg-muted" };
                        return (
                          <span
                            key={t.taal}
                            title={`${taal(t.taal)}: ${s.label}${t.reden ? ` (${t.reden})` : ""}`}
                            className={`rounded px-2 py-0.5 text-xs ${s.cls} ${actief.includes(t.taal) ? "font-bold" : ""}`}
                          >
                            {t.taal} · {s.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Blok>

        <Blok titel="Feedback uit de chat" uitleg="Laatste 50 berichten, zonder naam of nummer">
          {data.feedback.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-36">Wanneer</TableHead>
                  <TableHead className="w-28">Oordeel</TableHead>
                  <TableHead className="w-24">Taal</TableHead>
                  <TableHead>Wat ze schreven</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.feedback.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{f.tijd}</TableCell>
                    <TableCell className="text-sm">{f.oordeel}</TableCell>
                    <TableCell className="text-sm">{taal(f.taal)}</TableCell>
                    <TableCell className="text-sm">{f.tekst}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Leeg>Nog geen feedback.</Leeg>
          )}
        </Blok>
      </div>
    </main>
  );
}

const Admin = () => {
  const [sessie, setSessie] = useState<Session | null | undefined>(undefined);
  const [data, setData] = useState<Overzicht | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [geenToegang, setGeenToegang] = useState(false);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSessie(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSessie(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const laad = useCallback(async (s: Session) => {
    setBezig(true);
    setFout(null);
    try {
      const res = await fetch(ADMIN_URL, { headers: { Authorization: `Bearer ${s.access_token}` } });
      if (res.status === 403) {
        setGeenToegang(true);
        return;
      }
      const body = await res.json();
      if (!res.ok) throw new Error(body?.fout ?? `Fout ${res.status}`);
      setGeenToegang(false);
      setData(body as Overzicht);
    } catch (e) {
      setFout(e instanceof Error ? e.message : "Ophalen mislukt");
    } finally {
      setBezig(false);
    }
  }, []);

  useEffect(() => {
    if (sessie) laad(sessie);
    else {
      setData(null);
      setGeenToegang(false);
    }
  }, [sessie, laad]);

  const head = (
    <Head>
      <title>ChatBuddy beheer</title>
      <meta name="robots" content="noindex, nofollow" />
    </Head>
  );

  if (sessie === undefined) {
    return (
      <>
        {head}
        <main className="flex min-h-screen items-center justify-center bg-background">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </main>
      </>
    );
  }

  if (!sessie) {
    return (
      <>
        {head}
        <Inloggen />
      </>
    );
  }

  if (geenToegang) {
    return (
      <>
        {head}
        <main className="flex min-h-screen items-center justify-center bg-background px-4">
          <Card className="w-full max-w-sm p-6 text-sm">
            <h1 className="text-xl font-semibold">Geen toegang</h1>
            <p className="mt-3">{sessie.user.email} heeft geen toegang tot het beheer.</p>
            <Button className="mt-4" variant="outline" onClick={() => supabase.auth.signOut()}>
              Uitloggen
            </Button>
          </Card>
        </main>
      </>
    );
  }

  if (!data) {
    return (
      <>
        {head}
        <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-4 text-sm">
          {fout ? (
            <>
              <p>{fout}</p>
              <Button variant="outline" onClick={() => laad(sessie)}>
                Opnieuw proberen
              </Button>
            </>
          ) : (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          )}
        </main>
      </>
    );
  }

  return (
    <>
      {head}
      <Paneel data={data} bezig={bezig} vernieuw={() => laad(sessie)} email={sessie.user.email ?? ""} />
      {fout && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 rounded bg-destructive px-3 py-2 text-sm text-destructive-foreground">
          {fout}
        </div>
      )}
    </>
  );
};

export default Admin;
